import { useCallback, useEffect, useRef, useState } from 'react';

type AudioState = 'ready' | 'blocked' | 'unsupported';

// A short three-note chime followed by silence, repeating every three seconds.
// Looping in Web Audio keeps the alarm independent of background-tab JS timers.
const createChime = (context: AudioContext) => {
    const buffer = context.createBuffer(1, context.sampleRate * 3, context.sampleRate);
    const samples = buffer.getChannelData(0);

    [880, 1174.66, 1567.98].forEach((frequency, index) => {
        const start = Math.floor(index * 0.12 * context.sampleRate);
        const length = Math.floor(0.18 * context.sampleRate);
        for (let i = 0; i < length; i++) {
            const time = i / context.sampleRate;
            const envelope = Math.min(time / 0.02, (0.18 - time) / 0.08, 1);
            samples[start + i] += Math.sin(2 * Math.PI * frequency * time) * envelope * 0.25;
        }
    });

    return buffer;
};

const useOrderAlarm = (hasUnconfirmedOrders: boolean) => {
    const contextRef = useRef<AudioContext | null>(null);
    const chimeRef = useRef<AudioBuffer | null>(null);
    const alarmRef = useRef<AudioBufferSourceNode | null>(null);
    const notificationRef = useRef<AudioBufferSourceNode | null>(null);
    const [audioState, setAudioState] = useState<AudioState>('blocked');

    const resumeAudio = useCallback(() => {
        const context = contextRef.current;
        if (!context || context.state === 'closed') return;
        const updateState = () => {
            if (contextRef.current === context) {
                setAudioState(context.state === 'running' ? 'ready' : 'blocked');
            }
        };
        if (context.state === 'running') {
            updateState();
        } else {
            // Some browsers leave this promise pending until a user gesture.
            // Playback is controlled by the effect below, never this callback.
            void context.resume().then(updateState).catch(updateState);
        }
    }, []);

    useEffect(() => {
        const AudioContextClass = window.AudioContext ||
            (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioContextClass) {
            setAudioState('unsupported');
            return;
        }

        let context: AudioContext;
        try {
            context = new AudioContextClass();
        } catch {
            setAudioState('unsupported');
            return;
        }
        contextRef.current = context;
        const onStateChange = () => {
            setAudioState(context.state === 'running' ? 'ready' : 'blocked');
        };
        context.addEventListener('statechange', onStateChange);
        document.addEventListener('click', resumeAudio, true);
        document.addEventListener('keydown', resumeAudio, true);
        resumeAudio();

        return () => {
            document.removeEventListener('click', resumeAudio, true);
            document.removeEventListener('keydown', resumeAudio, true);
            context.removeEventListener('statechange', onStateChange);
            notificationRef.current?.stop();
            notificationRef.current?.disconnect();
            notificationRef.current = null;
            contextRef.current = null;
            chimeRef.current = null;
            void context.close().catch(() => {});
        };
    }, [resumeAudio]);

    const startChime = useCallback((loop: boolean) => {
        const context = contextRef.current;
        if (!context || context.state !== 'running') return null;
        if (!chimeRef.current) chimeRef.current = createChime(context);
        const source = context.createBufferSource();
        source.buffer = chimeRef.current;
        source.loop = loop;
        source.connect(context.destination);
        source.start();
        return source;
    }, []);

    useEffect(() => {
        if (!hasUnconfirmedOrders || audioState !== 'ready') return;
        notificationRef.current?.stop();
        const alarm = startChime(true);
        alarmRef.current = alarm;

        return () => {
            alarm?.stop();
            alarm?.disconnect();
            alarmRef.current = null;
        };
    }, [hasUnconfirmedOrders, audioState, startChime]);

    const playNotificationSound = useCallback(() => {
        if (alarmRef.current || notificationRef.current) return;
        const notification = startChime(false);
        if (!notification) return;
        notificationRef.current = notification;
        notification.onended = () => {
            notification.disconnect();
            if (notificationRef.current === notification) notificationRef.current = null;
        };
    }, [startChime]);

    return { audioState, resumeAudio, playNotificationSound };
};

export default useOrderAlarm;
